import { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin } from 'lucide-react';
import axios from 'axios';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let scriptLoaded = false;
let scriptLoading = false;
const loadCallbacks = [];

function loadGoogleMapsScript() {
  return new Promise((resolve) => {
    if (window.google?.maps?.places) { resolve(); return; }
    if (scriptLoaded) { resolve(); return; }
    loadCallbacks.push(resolve);
    if (scriptLoading) return;
    scriptLoading = true;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      loadCallbacks.forEach(cb => cb());
      loadCallbacks.length = 0;
    };
    document.head.appendChild(script);
  });
}

export default function AddressAutocomplete({ value, onChange, name = 'address', placeholder = 'Start typing your address...', className, testId }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [fsqSuggestions, setFsqSuggestions] = useState([]);
  const [showFsq, setShowFsq] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) return;
    // Defer Maps loading — triggered by handleFocus instead of mount
  }, []);

  const handleFocus = useCallback(() => {
    if (!GOOGLE_MAPS_KEY || ready) return;
    loadGoogleMapsScript().then(() => setReady(true));
  }, [ready]);

  useEffect(() => {
    if (!ready || !inputRef.current || autocompleteRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'us' },
      types: ['address'],
      fields: ['formatted_address'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place?.formatted_address) {
        onChange({ target: { name, value: place.formatted_address } });
        setShowFsq(false);
      }
    });

    autocompleteRef.current = autocomplete;
  }, [ready, name, onChange]);

  // Foursquare supplement — fetch when user types and Google hasn't resolved
  const fetchFoursquare = useCallback((query) => {
    if (!query || query.length < 3) { setFsqSuggestions([]); setShowFsq(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/places/autocomplete`, { params: { q: query } });
        if (data?.length > 0) {
          setFsqSuggestions(data);
          setShowFsq(true);
        }
      } catch {
        // Silently fail — Google is primary
      }
    }, 400);
  }, []);

  const handleInput = (e) => {
    onChange(e);
    fetchFoursquare(e.target.value);
  };

  const selectFsq = (suggestion) => {
    onChange({ target: { name, value: suggestion.address || suggestion.description } });
    setShowFsq(false);
    setFsqSuggestions([]);
  };

  // Fallback to plain input if no API key
  if (!GOOGLE_MAPS_KEY) {
    return (
      <div className="relative">
        <input
          type="text"
          name={name}
          value={value}
          onChange={handleInput}
          placeholder={placeholder}
          className={className}
          data-testid={testId}
        />
        {showFsq && fsqSuggestions.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 bg-white border border-black/10 shadow-lg mt-1" data-testid="fsq-suggestions">
            {fsqSuggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => selectFsq(s)}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 border-b border-zinc-100 last:border-0">
                <MapPin className="w-3 h-3 inline mr-2 text-zinc-400" />
                {s.description}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={value}
        onChange={handleInput}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
      />
      {ready && (
        <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
      )}
      {showFsq && fsqSuggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 bg-white border border-black/10 shadow-lg mt-1" data-testid="fsq-suggestions">
          <p className="px-4 py-1 text-[10px] text-zinc-400 uppercase tracking-wider bg-zinc-50">More suggestions</p>
          {fsqSuggestions.map((s, i) => (
            <button key={i} type="button" onClick={() => selectFsq(s)}
              className="block w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 border-b border-zinc-100 last:border-0">
              <MapPin className="w-3 h-3 inline mr-2 text-zinc-400" />
              {s.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { loadGoogleMapsScript };
